import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type OrderWithContact } from '@/data/mockOrdersData'
import { formatDistanceToNow } from 'date-fns'
import { MoreHorizontal, Phone, Mail, MessageCircle, ShoppingCart } from 'lucide-react'
import { formatSmartCurrency } from '@/utils/formatCurrency'

interface OrdersTableProps {
	orders: OrderWithContact[]
	onSelectOrder: (order: OrderWithContact) => void
	selectedOrderId?: string
	isLoading?: boolean
}

const getStageColor = (stage: string) => {
	switch (stage) {
		case 'new':
			return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
		case 'contacted':
			return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
		case 'qualified':
			return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
		case 'proposal':
			return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
		case 'won':
			return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
		case 'lost':
			return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
		default:
			return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
	}
}

const getStageLabel = (stage: string) => {
	return stage.charAt(0).toUpperCase() + stage.slice(1)
}

const formatValue = (cents: number) => {
	if (cents === 0) return '-'
	return formatSmartCurrency(cents)
}

const getPreferredChannelIcon = (channels: string[]) => {
	if (channels.includes('phone')) return <Phone className="h-3 w-3" />
	if (channels.includes('email')) return <Mail className="h-3 w-3" />
	if (channels.includes('text')) return <MessageCircle className="h-3 w-3" />
	return null
}

export function OrdersTable({ orders, onSelectOrder, selectedOrderId, isLoading }: OrdersTableProps) {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-32">
				<div className="text-sm text-muted-foreground">Loading orders...</div>
			</div>
		)
	}

	return (
		<Table>
			<TableHeader className="border-t border-t-border border-b border-b-border [&_tr]:!border-0">
				<TableRow>
					<TableHead>Contact</TableHead>
					<TableHead>Stage</TableHead>
					<TableHead>Last Activity</TableHead>
					<TableHead>Value</TableHead>
					<TableHead>Source</TableHead>
					<TableHead className="w-[50px]"></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-b-border">
				{orders.length === 0 ? (
					<TableRow className="!border-b !border-b-border">
						<TableCell colSpan={6} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-full py-8">
								<ShoppingCart className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No orders yet</p>
								<p className="text-xs text-muted-foreground">Orders will appear here when they're created</p>
							</div>
						</TableCell>
					</TableRow>
				) : (
					orders.map((order) => (
						<TableRow
							key={order.id}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-border ${
								selectedOrderId === order.id ? 'bg-muted' : ''
							}`}
							onClick={() => onSelectOrder(order)}
						>
							<TableCell>
								<div className="flex items-center gap-3">
									<Avatar className="h-8 w-8">
										<AvatarImage src="" alt={order.contact.display_name} />
										<AvatarFallback>
											{order.contact.display_name
												.split(' ')
												.map((n) => n[0])
												.join('')
												.toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div>
										<div className="font-medium">{order.contact.display_name}</div>
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											{order.contact.email && (
												<span className="truncate max-w-[200px]">{order.contact.email}</span>
											)}
											{order.contact.phone_raw && (
												<span className="text-xs">{order.contact.phone_raw}</span>
											)}
											{getPreferredChannelIcon(order.contact.preferred_channels)}
										</div>
									</div>
								</div>
							</TableCell>
							<TableCell>
								<Badge className={getStageColor(order.stage)}>
									{getStageLabel(order.stage)}
								</Badge>
							</TableCell>
							<TableCell>
								<div className="text-sm">
									{order.lastActivity ? (
										<div>
											<div className="font-medium capitalize">{order.lastActivity.type}</div>
											<div className="text-muted-foreground">
												{formatDistanceToNow(new Date(order.lastActivity.created_at), { addSuffix: true })}
											</div>
										</div>
									) : (
										<div className="text-muted-foreground">No activity</div>
									)}
								</div>
							</TableCell>
							<TableCell>
								<div className="font-medium">{formatValue(order.value_cents)}</div>
							</TableCell>
							<TableCell>
								<div className="text-sm">
									<span className="text-muted-foreground">{order.source}</span>
								</div>
							</TableCell>
							<TableCell>
								<Button 
									variant="ghost" 
									size="icon" 
									className="opacity-0 group-hover:opacity-100 transition-opacity"
									onClick={(e) => {
										e.stopPropagation()
										onSelectOrder(order)
									}}
								>
									<MoreHorizontal className="h-4 w-4" />
								</Button>
							</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</Table>
	)
}
